import type {
    AiAgentFormId,
    AiAgentIntent,
    AiAgentUiActionPayload,
    AiAgentUiActionType,
} from '../../../redux/aiAgent/aiAgent.type';
import store from '../../../store';

/**
 * Client mirror of server SYSTEM_SCHEMA.intentMeta — only what the UI needs to route
 * an action. Add a new action here (+ a form applicator) instead of hardcoding it.
 */
type AgentIntentMeta = {
    formId: AiAgentFormId;
    open: {
        type: AiAgentUiActionType;
        route?: string;
        openTicketPopup?: boolean;
    };
    requiresEventTypes: boolean;
    /** Mirrors server intentMeta.requiresEvent — account scope rejects it. */
    requiresEvent: boolean;
};

export const AGENT_INTENT_META: Record<AiAgentIntent, AgentIntentMeta> = {
    CREATE_EVENT: {
        formId: 'NEW_EVENT',
        open: {type: 'OPEN_UI'},
        requiresEventTypes: true,
        requiresEvent: false,
    },
    CREATE_TICKET: {
        formId: 'CREATE_TICKET',
        open: {type: 'NAVIGATE', route: '/tickets', openTicketPopup: true},
        requiresEventTypes: false,
        requiresEvent: true,
    },
};

/**
 * Whether the server would accept this intent in the given scope (mirrors its
 * scopes.allowedIntents) — so the UI never offers an action that is bound to be refused.
 */
export const isIntentAvailableInScope = (
    intent: AiAgentIntent,
    scope: 'account' | 'event',
): boolean => scope === 'event' || !AGENT_INTENT_META[intent].requiresEvent;

export const getIntentMeta = (intent: string | null | undefined): AgentIntentMeta | null => {
    if(!intent || !(intent in AGENT_INTENT_META)) {
        return null;
    }
    return AGENT_INTENT_META[intent as AiAgentIntent];
};

export const getIntentForFormId = (formId: string | null | undefined): AiAgentIntent | null => {
    if(!formId) {
        return null;
    }
    const entry = Object.entries(AGENT_INTENT_META).find(([, meta]) => meta.formId === formId);
    return (entry?.[0] as AiAgentIntent) ?? null;
};

export const getFormIdForIntent = (intent: AiAgentIntent): AiAgentFormId =>
    AGENT_INTENT_META[intent].formId;

/** Intent that opens a create-event wizard via OPEN_UI + eventType. */
export const getEventTypeIntent = (): AiAgentIntent | null => {
    const entry = Object.entries(AGENT_INTENT_META).find(([, meta]) => meta.requiresEventTypes);
    return (entry?.[0] as AiAgentIntent) ?? null;
};

/** Locked chat intent, or intent of the form currently on screen. */
export const getEffectiveSessionIntent = (): AiAgentIntent | null => {
    const {aiAgent} = store.getState();
    return aiAgent?.sessionIntent ?? getIntentForFormId(aiAgent?.activeFormId);
};

/**
 * Which intent a uiAction runs. The server already sanitizes payload.intent/formId
 * against the allowed intents, so the payload is the only source needed here.
 * eventType alone implies create-event (same as applyAgentUiAction OPEN_UI).
 */
export const getPayloadIntent = (payload: AiAgentUiActionPayload = {}): AiAgentIntent | null => {
    if(payload.intent && payload.intent in AGENT_INTENT_META) {
        return payload.intent;
    }
    const fromFormId = getIntentForFormId(payload.formId);
    if(fromFormId) {
        return fromFormId;
    }
    return payload.eventType ? getEventTypeIntent() : null;
};

/** Intent whose open uses openTicketPopup (e.g. CREATE_TICKET). */
export const getNavigatePopupIntent = (): AiAgentIntent | null => {
    const entry = (Object.keys(AGENT_INTENT_META) as AiAgentIntent[]).find(
        (intent) => !!getIntentMeta(intent)?.open?.openTicketPopup,
    );
    return entry ?? null;
};

export const wantsNavigatePopup = (payload: AiAgentUiActionPayload | undefined): boolean => {
    if(!payload) {
        return false;
    }
    const popupIntent = getNavigatePopupIntent();
    if(!popupIntent) {
        return false;
    }
    const open = getIntentMeta(popupIntent)?.open;
    return payload.intent === popupIntent
        || payload.formId === getFormIdForIntent(popupIntent)
        || !!payload.openTicketPopup
        || (!!open?.route && payload.route === open.route);
};

/** True when navigate-popup open is allowed for the current session lock. */
export const canOpenNavigatePopup = (): boolean => {
    const popupIntent = getNavigatePopupIntent();
    if(!popupIntent) {
        return false;
    }
    const locked = getEffectiveSessionIntent();
    return !locked || locked === popupIntent;
};
